import { useEffect } from 'react';
import { registerLocale } from 'react-datepicker';
import ReactGA from 'react-ga4';
import { Route, Routes, useLocation } from 'react-router-dom';
import { themeColors } from '@ftrprf/tailwind-components';
import nl from 'date-fns/locale/nl-BE';
import fr from 'date-fns/locale/fr';
import { ErrorBoundaryWrapper } from 'components/ErrorBoundaryWrapper';
import { Error404 } from 'pages/Error/Error404';
import { ExerciseContainer } from 'pages/Exercise/student/ExerciseContainer';
import OpenExercise from 'pages/Exercise/teacher/OpenExercise';
import { HelpRedirect, HELPTYPES } from 'pages/HelpVideos/HelpRedirect';
import { IntroPage } from 'pages/IntroPage';
import PrivacyPolicy from 'pages/PrivacyPolicy/PrivacyPolicy';
import { MultiScreenMode } from 'pages/SlideViewer/MultiScreenMode';
import SlideViewerContainer from 'pages/SlideViewer/SlideViewerContainer';
import { IDPS } from 'utils/constants/idp';
import URLS from 'utils/constants/urls';
import SlideviewerLayout from './layouts/SlideviewerLayout';
import GeneralLayout from './layouts/GeneralLayout';
import FullPageLayout from './layouts/FullPageLayout';

export default function App() {
  const location = useLocation();
  useEffect(() => {
    ReactGA.send({ hitType: 'pageview', page: location.pathname });
  }, [location]);

  useEffect(() => {
    if (process.env.NODE_ENV !== 'development') {
      ReactGA.initialize('G-0XZMQ7JR66');
    }

    registerLocale('nl', nl);
    registerLocale('fr', fr);
  }, []);

  useEffect(() => {
    // Set CSS variable to ::root to the brand colors for the selected IDP
    const currentColors = themeColors[IDPS.FTRPRF];

    Object.keys(currentColors).forEach((key) => {
      document.documentElement.style.setProperty(
        `--accent-color-${key}`,
        currentColors[key],
      );
    });
  }, []);

  return (
    <ErrorBoundaryWrapper>
      <div className="antialiased w-full h-full flex flex-col flex-grow items-center relative">
        <Routes>
          <Route
            element={
              <SlideviewerLayout>
                <SlideViewerContainer />
              </SlideviewerLayout>
            }
            path={URLS.SLIDEVIEWER_OVERVIEW_SLIDE_VIEWMODE_SLIDEID}
          />
          <Route
            element={
              <SlideviewerLayout>
                <SlideViewerContainer />
              </SlideviewerLayout>
            }
            path={URLS.SLIDEVIEWER_OVERVIEW}
          />
          <Route
            element={
              <SlideviewerLayout>
                <SlideViewerContainer />
              </SlideviewerLayout>
            }
            path={URLS.SLIDEVIEWER_OVERVIEW_VIEWMODE}
          />
          <Route
            element={<HelpRedirect type={HELPTYPES.CATEGORY} />}
            path={URLS.HELP_CATEGORY}
          />
          <Route
            element={<HelpRedirect type={HELPTYPES.VIDEO} />}
            path={URLS.HELP_VIDEO}
          />
          <Route element={<HelpRedirect />} path={URLS.HELP} />
          <Route element={<MultiScreenMode />} path={URLS.MULTI_SCREEN} />
          <Route
            element={
              <FullPageLayout>
                <ExerciseContainer />
              </FullPageLayout>
            }
            path={URLS.EXERCISE_NEW}
          />
          <Route
            element={
              <FullPageLayout>
                <ExerciseContainer />
              </FullPageLayout>
            }
            path={URLS.EXERCISE_DETAIL}
          />
          <Route
            element={
              <FullPageLayout>
                <ExerciseContainer />
              </FullPageLayout>
            }
            path={URLS.EXERCISE_START}
          />
          <Route
            element={
              <FullPageLayout>
                <ExerciseContainer />
              </FullPageLayout>
            }
            path={URLS.EXERCISE_START_EXERCISEVERSIONID}
          />
          <Route
            element={
              <FullPageLayout>
                <OpenExercise />
              </FullPageLayout>
            }
            path={URLS.EXERCISE_OPEN}
          />
          <Route element={<IntroPage />} path={URLS.ROOT} />
          <Route
            element={
              <GeneralLayout>
                <PrivacyPolicy />
              </GeneralLayout>
            }
            path={URLS.TERMS_OF_SERVICE}
          />
          <Route
            element={
              <GeneralLayout>
                <PrivacyPolicy />
              </GeneralLayout>
            }
            path={URLS.PRIVACY_POLICY}
          />
          <Route
            element={
              <GeneralLayout>
                <Error404 />
              </GeneralLayout>
            }
            path="*"
          />
        </Routes>
      </div>
    </ErrorBoundaryWrapper>
  );
}
